import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { StreamingUgcPoint } from 'fym-shared/src/music24/streamingSections';
import { TypedMemo } from '../../../../common/types';
import { Segmented } from '../../../components/primitives/Segmented';
import { formatDay } from '../../streamingFormat';
import { UGC_DAY_PLATFORMS, ugcDayBars, UgcDayMetric } from '../../periodSections';
import { shortVideoMessages as messages } from '../../shortVideoMessages';
import { periodSeriesColor } from '../../period/periodColors';
import { StreamingBlock } from '../../parts/StreamingBlock';
import { UGC_NUMBER_LABELS } from './UgcTiles';
import { UGC_PLATFORM_LOOKS } from './ugcPlatforms';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the period sections; each part uses its own slice of it */
import styles from '../../StreamingPeriod.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface UgcDayBarsProps {
  series: readonly StreamingUgcPoint[];
}

/**
 * The days of the period, split by platform (design streaming-sections c1). Only drawn when the
 * source gives days; a source with months only has no chart. A platform with no number on a day
 * has no bar part; a 0 is a real zero part.
 */
const UgcDayBarsComponent: React.FC<UgcDayBarsProps> = ({ series }) => {
  const intl = useIntl();
  const [metric, setMetric] = useState<UgcDayMetric>('creations');
  const days = useMemo(() => ugcDayBars(series, metric), [metric, series]);
  const options = useMemo(
    () =>
      (['creations', 'views', 'likes'] as const).map((value) => ({
        value,
        label: intl.formatMessage(UGC_NUMBER_LABELS[value]),
      })),
    [intl]
  );

  return (
    <StreamingBlock title={intl.formatMessage(messages.perDay)} testId="streaming-shortVideo-days">
      <Segmented options={options} value={metric} onChange={setMetric} />
      <div className={styles.dayBars} role="list">
        {days.map((day) => (
          <div key={day.date} className={styles.dayBar} role="listitem" title={formatDay(intl, day.date)}>
            {day.parts.map((part, index) =>
              part.height === null ? null : (
                <span
                  key={part.platform}
                  className={styles.stackPart}
                  data-platform={part.platform}
                  style={{ height: `${part.height}%`, background: periodSeriesColor(index) }}
                />
              )
            )}
          </div>
        ))}
      </div>
      <div className={styles.legend}>
        {UGC_DAY_PLATFORMS.map((platform, index) => (
          <span key={platform}>
            <span className={styles.swatch} style={{ background: periodSeriesColor(index) }} />
            {UGC_PLATFORM_LOOKS[platform].shortName}
          </span>
        ))}
      </div>
    </StreamingBlock>
  );
};

export const UgcDayBars = TypedMemo(UgcDayBarsComponent);
